<template>
  <div class="loyalty-levels-container">
    <div class="grid-container">
      <div class="image-container">
        <Picture :images="image" />
      </div>
      <div class="level-container">
        <h6>{{ level.name }}</h6>
        <p v-if="!level.secret" class="points">{{ pointsRange }} POINTS</p>
        <div v-else class="padlock-icon">
          <i class="icon icon-lock" />
        </div>
      </div>
      <div class="accordion-container">
        <Accordion
          v-for="(benefit, index) in levelBenefits"
          :key="index"
          class="accordion-group"
          :show-arrow="true"
        >
          <template #header>{{ benefit.header }}</template>
          <template #body>{{ benefit.body }}</template>
        </Accordion>
        <slot />
      </div>
    </div>
    <div class="sign-up-button">
      <Button
        v-if="!isLoggedIn"
        type="button"
        full-width
        theme="outline"
        @click="setSlideSignUp(true)"
      >
        SIGN UP TO MEMBERS ONLY
      </Button>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import Picture from "@/components/Picture.vue";
import Accordion from "@/components/Accordion.vue";
import loyaltyTiersContent from "@/assets/json/loyalty-tiers.json";
import loyaltyQueryV2 from "@/graphql/queries/LoyaltyV2.gql";
import { mapGetters, mapActions, mapState } from "vuex";

export default {
  name: "LoyaltyLevels",
  components: {
    Button,
    Picture,
    Accordion,
  },
  props: {
    level: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      customer: null,
      loyaltyTiers: loyaltyTiersContent,
    };
  },
  computed: {
    ...mapState("auth", ["customerAccessToken"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    pointsRange() {
      const lower = this.level.lowerBound[this.currencyCode];
      const upper = this.level.upperBound[this.currencyCode];

      if (lower && upper) {
        return lower + " - " + upper;
      }

      if (lower) {
        lower + " +";
      }

      if (upper) {
        return 0 + " - " + upper;
      }

      return false;
    },
    image() {
      const imageName = this.level.name.replace(" ", "-").toLowerCase();
      const image = require(`@/assets/images/loyalty/${imageName}-card.png`);
      return {
        smallMedia: image,
      };
    },
    levelBenefits() {
      let benefits = this.level.benefits;

      if (this.level.secret && !this.nextTier.secret) {
        benefits = benefits.map(() => ({
          header: "Secret benefit",
          body: "Reach the next level to find out more",
        }));
      }

      return benefits;
    },
    nextTier() {
      const currentTier = this.loyaltyTiers.tiers.find((tier) => {
        return tier.name === this.tierName;
      });

      const order = currentTier.order;
      const nextTierOrder = order + 1;

      const nextTier = this.loyaltyTiers.tiers.find(
        (tier) => tier.order === nextTierOrder
      );

      return nextTier;
    },
    tierName() {
      if (this.customer?.tierName?.value) {
        return this.customer?.tierName?.value;
      }

      const tags = this.customer?.tags || [];
      if (tags.length > 0) {
        const tierTag = tags.find((tag) => tag.indexOf("tier") !== -1);
        return tierTag.replace("tier: ", "");
      }

      return "Level 1";
    },
  },
  methods: {
    ...mapActions("app", ["setSlideSignUp"]),
  },
  apollo: {
    customer: {
      query: loyaltyQueryV2,
      skip() {
        return !this.customerAccessToken?.value;
      },
      variables() {
        return {
          accessToken: this.customerAccessToken?.value,
        };
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.loyalty-levels-container {
  padding-bottom: 20px;
}

.grid-container {
  max-width: 922px;
  margin: 0 auto;

  @include respond-above(md) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      "level level"
      "image accordion";

    grid-column-gap: 40px;
  }
}

.image-container {
  grid-area: image;
}

.level-container {
  grid-area: level;
}
.accordion-container {
  grid-area: accordion;
}

.padlock-icon {
  text-align: center;
  margin-bottom: 34px;
  @include respond-above(md) {
    margin-bottom: 40px;
  }
}

h6 {
  font-family: $heading-font-family;
  font-size: 16px;
  font-weight: 400;
  margin: 20px 0 10px;
  text-align: center;
  text-transform: uppercase;
  @include respond-above(md) {
    font-size: 40px;
  }
}

.points {
  font-family: $body-font-family;
  font-size: 13px;
  font-weight: 400;
  text-align: center;
  margin-bottom: 20px;
  @include respond-above(md) {
    margin-bottom: 40px;
  }
}
.sign-up-button {
  margin: 20px 0 20px;
  @include respond-above(md) {
    display: none;
  }
}

.accordion-group {
  border-bottom: 1px solid #d4d4d4;
  ::v-deep(.accordion__header) {
    background: none;
    outline: none;
    border: none;
    padding: 14px 0;
    font-family: $body-font-family;
    font-size: 13px;
    font-weight: 400;
    width: 100%;
    text-align: left;
  }
  ::v-deep(.accordion__body) {
    display: block;
    font-family: $body-font-family;
    font-size: 13px;
    height: auto;
    padding-bottom: 14px;
  }
}
</style>
